import React from 'react';
import Icon from '../../../components/AppIcon';

const RiskContributionPanel = ({ selectedAgent, timeRange }) => {
  const riskMetrics = selectedAgent ? {
    agentId: selectedAgent?.id,
    agentName: selectedAgent?.name,
    metrics: [
      {
        category: 'Sanctions & PEP',
        icon: 'ShieldAlert',
        color: 'text-error',
        bgColor: 'bg-error/10',
        items: [
          { label: 'PEP Alerts', value: '3', per1k: '1.2/1K' },
          { label: 'Sanctions Hits', value: '1', per1k: '0.4/1K' }
        ]
      },
      {
        category: 'Fraud Detection',
        icon: 'AlertTriangle',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        items: [
          { label: 'Rules Triggered', value: '8', flagRate: '2.8%' },
          { label: 'Auto-flagged', value: '5', flagRate: '1.7%' }
        ]
      },
      {
        category: 'Compliance Escalations',
        icon: 'FileWarning',
        color: 'text-accent',
        bgColor: 'bg-accent/10',
        items: [
          { label: 'SARs/STRs Linked', value: '2' },
          { label: 'Manual Reviews', value: '12' }
        ]
      },
      {
        category: 'Transaction Issues',
        icon: 'XCircle',
        color: 'text-error',
        bgColor: 'bg-error/10',
        items: [
          { label: 'Chargebacks', value: '4', ratio: '0.14%' },
          { label: 'Reversals', value: '7', ratio: '0.24%' }
        ]
      },
      {
        category: 'High-Risk Corridors',
        icon: 'MapPin',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        items: [
          { label: 'Exposure Index', value: '45', risk: 'Medium' },
          { label: 'Risk Corridors', value: '2/3', details: 'US→MX, US→GT' }
        ]
      },
      {
        category: 'Account Security',
        icon: 'Lock',
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        items: [
          { label: 'Takeover Threats', value: '0' },
          { label: 'Device Anomalies', value: '3', rate: '1.0%' }
        ]
      }
    ]
  } : null;

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Shield" size={20} />
          <span>Risk Contribution Metrics</span>
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {selectedAgent ? `Risk profile for ${selectedAgent?.name}` : 'Select an agent to view risk metrics'}
        </p>
      </div>
      <div className="p-4">
        {!selectedAgent ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Icon name="MousePointerClick" size={32} color="currentColor" className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">
              Select an agent from the table to view detailed risk contribution metrics
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {riskMetrics?.metrics?.map((metric, index) => (
              <div key={index} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${metric?.bgColor} flex items-center justify-center`}>
                    <Icon name={metric?.icon} size={20} className={metric?.color} />
                  </div>
                  <h4 className="font-medium text-foreground">{metric?.category}</h4>
                </div>
                <div className="space-y-2 ml-13">
                  {metric?.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{item?.label}</span>
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${
                          parseInt(item?.value) > 5 ? 'text-error' :
                          parseInt(item?.value) > 2 ? 'text-warning': 'text-success'
                        }`}>
                          {item?.value}
                        </span>
                        {item?.per1k && (
                          <span className="text-xs text-muted-foreground">({item?.per1k})</span>
                        )}
                        {item?.flagRate && (
                          <span className="text-xs text-muted-foreground">({item?.flagRate})</span>
                        )}
                        {item?.ratio && (
                          <span className="text-xs text-muted-foreground">({item?.ratio})</span>
                        )}
                        {item?.risk && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            item?.risk === 'High' ? 'bg-error/10 text-error' :
                            item?.risk === 'Medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                          }`}>
                            {item?.risk}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {metric?.items?.some(item => item?.details) && (
                    <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                      {metric?.items?.find(item => item?.details)?.details}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Overall Risk Score */}
            <div className="border-2 border-primary/20 rounded-lg p-4 bg-primary/5">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-foreground">Overall Risk Score</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Based on all risk factors
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">23.5</div>
                  <div className="text-sm text-success flex items-center justify-end space-x-1">
                    <Icon name="TrendingDown" size={14} />
                    <span>-2.1 pts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskContributionPanel;